import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  CircularProgress,
  Avatar,
  Chip,
  Alert,
  Stack,
} from '@mui/material';
import {
  Check as CheckIcon,
  Close as CloseIcon,
  AccessTime as AccessTimeIcon,
  Person as PersonIcon,
  Notifications as NotificationsIcon,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { zhCN } from 'date-fns/locale';
import { 
  emergencyCallPendingInvitationService,
  emergencyCallInvitationService,
  IPendingInvitationInfo,
  user,
  AppModelEvent,
  appModel
} from '@app/model';
import { useProxyWatch } from '@fanfanlo';
import { matrixClient } from '@im/matrix';

// 邀请卡片组件
function PendingInvitationCard({ 
  invitation,
  onRespond,
  isProcessing = false,
}: { 
  invitation: IPendingInvitationInfo;
  onRespond?: (action: 'accept' | 'decline') => Promise<void>;
  isProcessing?: boolean;
}) {
  const [isProcessingAction, setIsProcessingAction] = useState(false);

  const handleRespond = async (action: 'accept' | 'decline') => {
    if (!onRespond) return;
    
    try {
      setIsProcessingAction(true);
      await onRespond(action);
    } finally {
      setIsProcessingAction(false);
    }
  };

  const isExpired = new Date(invitation.expires_at * 1000) < new Date();

  return (
    <Card variant="outlined" sx={{ mb: 2, opacity: isProcessing ? 0.7 : 1 }}>
      <CardContent>
        <Box display="flex" alignItems="center" mb={2}>
          <Avatar 
            src={invitation.user_info.avatar_url} 
            alt={invitation.user_info.display_name}
            sx={{ width: 50, height: 50, mr: 2 }}
          >
            {invitation.user_info.display_name?.[0]?.toUpperCase() || '?'}
          </Avatar>
          <Box flexGrow={1}>
            <Typography variant="h6">
              {invitation.user_info.display_name || '未知用户'}
            </Typography>
            {/* <Typography variant="body2" color="text.secondary">
              ID: {invitation.user_info.user_id}
            </Typography> */}
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {formatDistanceToNow(invitation.created_at * 1000, { 
                addSuffix: true, 
                locale: zhCN 
              })}
            </Typography>
          </Box>
          <Box textAlign="right">
            <Chip 
              size="small"
              icon={<AccessTimeIcon fontSize="small" />}
              label={isExpired ? '已过期' : `${formatDistanceToNow(invitation.expires_at * 1000, { 
                addSuffix: true, 
                locale: zhCN 
              })}`}
              color={isExpired ? 'error' : 'primary'}
              variant="outlined"
            />
          </Box>
        </Box>
        
        {invitation.message && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 1 }}>
            邀请信息: {invitation.message}
          </Typography>
        )}

        <Typography variant="body2" color="primary" sx={{ fontWeight: 'medium' }}>
          邀请您成为紧急联系人
        </Typography>
      </CardContent>
      
      {!isExpired && (
        <CardActions sx={{ justifyContent: 'flex-end', pt: 0 }}>
          <Button 
            size="small" 
            color="error"
            startIcon={<CloseIcon />}
            onClick={() => handleRespond('decline')}
            disabled={isProcessing || isProcessingAction}
          >
            拒绝
          </Button>
          <Button 
            size="small" 
            color="primary"
            variant="contained"
            startIcon={<CheckIcon />}
            onClick={() => handleRespond('accept')}
            disabled={isProcessing || isProcessingAction}
          >
            接受
          </Button>
        </CardActions>
      )}
    </Card>
  );
}

const PendingInvitationsList: React.FC = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [invitations, setInvitations] = useState<IPendingInvitationInfo[]>([]);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  // 监听用户登录状态
  const [userToken] = useProxyWatch(user, 'data.storeData.user_token', user.data.storeData.user_token);

  // 加载未处理的被邀请信息
  const loadPendingInvitations = useCallback(async () => {
    if (!userToken) {
      setInvitations([]);
      return;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      const res = await emergencyCallPendingInvitationService.listPendingReceivedInvitations({
        params: {},
      } as any);
      
      if (res.shellIsOk && res.dataIsOk && res.data?.is_ok) {
        setInvitations(res.data.invitations || []);
      } else {
        setError(res.data?.msg || '加载邀请列表失败');
      }
    } catch (err) {
      console.error('加载未处理邀请列表异常:', err);
      setError('发生未知错误');
    } finally {
      setIsLoading(false);
    }
  }, [userToken]); // 现在可以安全地依赖userToken了

  // 响应邀请
  const handleRespondToInvite = useCallback(async (invitationId: string, action: 'accept' | 'decline'): Promise<void> => {
    try {
      setIsProcessing(true);

      const res = await emergencyCallInvitationService.respondToInvite({
        params: { invitation_id: invitationId, action },
      } as any);

      if (!res.shellIsOk || !res.dataIsOk || !res.data?.is_ok) {
        throw new Error(res.data?.msg || '操作失败');
      }
      
      // 从列表中移除已处理的邀请
      setInvitations(prev => prev.filter(invite => invite.invitation_id !== invitationId));
      
      // 发送邀请列表更新事件
      appModel.dispatcher.emit(AppModelEvent.InviteListUpdated);
      
    } catch (err) {
      console.error('处理邀请失败:', err);
      setError(err instanceof Error ? err.message : '操作失败，请重试');
    } finally {
      setIsProcessing(false);
    }
  }, []);

  // 监听用户登录状态变化
  useEffect(() => {
    if (userToken) {
      // 有登录用户了，刷新邀请列表
      loadPendingInvitations();
    } else {
      // 清空邀请信息列表
      setInvitations([]);
    }
  }, [userToken]); // 只依赖userToken

  // 监听邀请列表更新事件
  useEffect(() => {
    const unsubscribe = appModel.dispatcher.addListener(AppModelEvent.InviteListUpdated, () => {
      console.log('AppModelEvent.InviteListUpdated - 刷新未处理邀请列表');
      loadPendingInvitations();
    });

    return unsubscribe;
  }, [loadPendingInvitations]); // 依赖loadPendingInvitations函数

  useEffect(()=>{
    const unsub = matrixClient.imDispatcher.addListener(matrixClient.ImDispatcherEventTypes.INVITE, ()=>{
      if(!userToken)return;
      loadPendingInvitations();
    });
    return unsub;
  }, [])
  // 如果没有登录用户，不显示任何内容
  if (!userToken) {
    return null;
  }

  if (isLoading) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" p={2}>
        {/* <CircularProgress size={24} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          加载中...
        </Typography> */}
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={2}>
        <Alert severity="error">
          {error}
        </Alert>
      </Box>
    );
  }

  if (invitations.length === 0) {
    return (
      <Box 
        display="flex" 
        flexDirection="column" 
        alignItems="center" 
        p={2}
        textAlign="center"
      >
        {/* <NotificationsIcon color="disabled" sx={{ fontSize: 48, mb: 1 }} />
        <Typography variant="body2" color="textSecondary">
          暂无未处理的邀请
        </Typography> */}
      </Box>
    );
  }

  return (
    <Box p={2}>
      <Box display="flex" alignItems="center" mb={2}>
        <NotificationsIcon color="primary" sx={{ mr: 1 }} />
        <Typography variant="h6" color="primary">
          未处理的邀请 ({invitations.length})
        </Typography>
      </Box>

      <Stack spacing={2}>
        {invitations.map((invitation) => (
          <PendingInvitationCard
            key={invitation.invitation_id}
            invitation={invitation}
            onRespond={(action) => handleRespondToInvite(invitation.invitation_id, action)}
            isProcessing={isProcessing}
          />
        ))}
      </Stack>
    </Box>
  );
};

export default PendingInvitationsList;